import { alpha, formControlClasses, inputClasses } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { blueGrey } from "../../../../mp-core/themes/palette"

type StylesParams = {
  size: "small" | "medium"
  variant: "outlined" | "filled"
  showLine: boolean
}

export const useStyles = makeStyles<StylesParams>()(
  ({ spacing, palette, typography, mixins }, { size, variant, showLine }) => ({
    root: {
      display: "inline-flex",
      cursor: "pointer",

      [`& .${inputClasses.root}`]: {
        border:
          variant === "outlined"
            ? `1px solid ${blueGrey[200]}`
            : `1px solid transparent`,
        borderRadius: "3px",
        minWidth: "250px",
        padding: size === "small" ? spacing(1, 3) : spacing(2, 3),
        alignItems: "center",
        backgroundColor:
          variant === "outlined"
            ? palette.common.white
            : alpha(blueGrey[50], 0.5),
        columnGap: spacing(2),
        cursor: "pointer",

        // The "open" / focused state — driven by the rootOpen class on the
        // FormControl wrapper, not native focus, since this trigger doesn't
        // capture keyboard focus into its input.
        [`&:hover:not(.${inputClasses.disabled})`]: {
          borderColor: blueGrey[400]
        },

        [`&.${inputClasses.disabled}`]: {
          borderColor: variant === "outlined" ? blueGrey[100] : "transparent",
          cursor: "not-allowed"
        }
      },

      [`& .${inputClasses.input}`]: {
        padding: 0,
        fontSize:
          size === "small"
            ? typography.body2.fontSize
            : typography.body1.fontSize,
        lineHeight:
          size === "small"
            ? typography.body2.lineHeight
            : typography.body1.lineHeight,
        height: size === "small" ? "1.125rem" : "1.625rem",
        boxSizing: "border-box",
        cursor: "pointer",
        userSelect: "none",
        color: palette.text.primary,

        [`&:focus`]: {
          outline: "none"
        },

        [`&::placeholder`]: {
          color: blueGrey[500],
          opacity: 1
        },

        [`&.${inputClasses.disabled}, &.${inputClasses.disabled}::placeholder`]:
          {
            color: blueGrey[300],
            WebkitTextFillColor: blueGrey[300]
          }
      },

      [`&:focus-within .${inputClasses.root}:not(.${inputClasses.disabled})`]:
        mixins.focusRing.outline
    },

    // Applied when the caller sets `open`. Override hover so the focused look
    // wins even when the cursor is over the trigger.
    rootOpen: {
      [`& .${inputClasses.root}`]: {
        ...mixins.focusRing.outline,

        [`&:hover:not(.${inputClasses.disabled})`]: {
          borderColor: palette.focus
        }
      }
    },

    // Same focused look, driven instead by the aria-expanded="true" attribute
    // that `Popup` injects on the cloned anchor when its popover is open.
    // Lets the trigger show the focused state for free when callers use the
    // children prop (children → Popup wrapping).
    rootExpanded: {
      [`&[aria-expanded='true'] .${inputClasses.root}`]: {
        ...mixins.focusRing.outline,

        [`&:hover:not(.${inputClasses.disabled})`]: {
          borderColor: palette.focus
        }
      }
    },

    // The greyed-out "disabled" look. We can't use the real `disabled` attribute
    // because disabled fields can't be focused, and we need keyboard focus to land
    // back here when the dropdown closes — so we paint the look ourselves instead.
    // (`&&` just makes these styles beat the hover styles above.)
    rootDisabled: {
      [`&& .${inputClasses.root}`]: {
        borderColor: variant === "outlined" ? blueGrey[100] : "transparent",
        cursor: "not-allowed",

        [`&:hover`]: {
          borderColor: variant === "outlined" ? blueGrey[100] : "transparent"
        }
      },

      [`& .${inputClasses.input}`]: {
        cursor: "not-allowed",
        color: blueGrey[300],
        WebkitTextFillColor: blueGrey[300],

        [`&::placeholder`]: {
          color: blueGrey[300],
          WebkitTextFillColor: blueGrey[300]
        }
      }
    },

    startIcon: {
      display: "inline-flex",
      alignItems: "center",
      color: blueGrey[400],
      marginRight: spacing(1),
      fontSize: 12,

      "& *": {
        color: blueGrey[400],
        fontSize: 12
      }
    },

    endAdornment: {
      display: "inline-flex",
      alignItems: "center",
      color: blueGrey[400],
      // Right-align the caret so, when the box is wider than its content
      // (minWidth), the slack falls before the caret instead of after it.
      marginLeft: "auto",
      fontSize: "0.875rem", // 14px
      ...(showLine && {
        paddingLeft: spacing(3)
      }),

      // Scope to the FaIcon (caret/spinner) only — the IconButton clear has
      // its own size scheme via its size prop, so we don't override it here.
      "& > .MuiIcon-root": {
        color: blueGrey[400],
        fontSize: "0.875rem",
        padding: spacing(2)
      }
    },

    // Tweaks the IconButton (xsmall) used for the clear action: drop its inner
    // icon to 11px and tint with blueGrey[400] to match the rest of the trigger.
    clearButton: {
      color: blueGrey[400],

      "& .MuiIcon-root": {
        fontSize: "0.6875rem"
      }
    },

    // Pin the search/breadcrumb header so options stay reachable when the popover is short at high zoom.
    popoverPaper: {
      [`& .${formControlClasses.root}`]: {
        position: "sticky",
        top: 0,
        zIndex: 1,
        backgroundColor: palette.common.white
      }
    }
  })
)
